import { Link } from 'react-router-dom';
import SizingTable from './SizingTable';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function WarningBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-error-warning-line text-amber-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-amber-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface FactorCardProps {
  icon: string;
  title: string;
  description: string;
}

function FactorCard({ icon, title, description }: FactorCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 flex gap-4 items-start hover:-translate-y-0.5 transition-transform duration-200">
      <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl shrink-0">
        <i className={`${icon} text-teal-500 text-xl`}></i>
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-sm mb-1">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const factors = [
  {
    icon: 'ri-group-line',
    title: 'Guest Count',
    description: 'The most important factor. Always size up slightly, as a tent that feels comfortably full looks better than one that feels cramped.',
  },
  {
    icon: 'ri-layout-2-line',
    title: 'Event Layout',
    description: 'Seated dining requires significantly more space per person than a standing cocktail function or festival crowd.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Venue Terrain',
    description: 'Uneven ground, slopes, trees, or existing structures may limit how large a tent can be rigged in that space.',
  },
  {
    icon: 'ri-cloud-windy-line',
    title: 'Weather Conditions',
    description: 'For high-wind or rainy regions, a smaller well-anchored tent is often more reliable than an oversized one.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipment & Staging',
    description: 'DJ booths, stages, catering tables, and bar setups all eat into usable floor space. Factor them in early.',
  },
  {
    icon: 'ri-links-line',
    title: 'Linking Multiple Tents',
    description: 'For 200+ guests, linking two or more tents is often more versatile than a single oversized structure.',
  },
];

interface EventTypeCardProps {
  title: string;
  recommended: string;
  note: string;
  icon: string;
}

function EventTypeCard({ title, recommended, note, icon }: EventTypeCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-xl shrink-0">
          <i className={`${icon} text-teal-500`}></i>
        </div>
        <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
      </div>
      <p className="text-teal-600 font-semibold text-sm mb-1">{recommended}</p>
      <p className="text-gray-500 text-xs leading-relaxed">{note}</p>
    </div>
  );
}

const eventTypes = [
  {
    icon: 'ri-heart-line',
    title: 'Weddings',
    recommended: '8m × 12m to 12m × 18m',
    note: 'Account for a dance floor, head table, bar, and catering station in addition to dining guests.',
  },
  {
    icon: 'ri-music-line',
    title: 'Festivals & Concerts',
    recommended: '12m × 18m or linked tents',
    note: 'Consider crowd flow, stage clearance, and separate zones for VIP, bar, and general admission.',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Corporate Events',
    recommended: '6m × 9m to 10m × 15m',
    note: 'Presentation screens, seating rows, and registration desks require extra clearance around the perimeter.',
  },
  {
    icon: 'ri-goblet-line',
    title: 'Cocktail Receptions',
    recommended: '5m × 8m to 8m × 12m',
    note: 'Standing events allow 0.7–1 m² per person. Go up a size if you\'re adding high tables or a live band.',
  },
  {
    icon: 'ri-sun-line',
    title: 'Garden Parties',
    recommended: '3m × 6m to 6m × 9m',
    note: 'Relaxed layout with ample spacing. One mid-size tent often anchors the space beautifully without overcrowding.',
  },
  {
    icon: 'ri-building-line',
    title: 'Brand Activations',
    recommended: '8m × 12m or custom skin',
    note: 'Custom printed skins double as a branding canvas. Size up to maximise logo visibility and impact.',
  },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      {/* Intro */}
      <Paragraph>
        Choosing the right stretch tent size can make or break your event experience. Too small and your guests feel cramped; too large and the space loses its intimacy and atmosphere. Whether you&apos;re planning a backyard birthday bash, a corporate brand activation, or a multi-day music festival, this guide breaks down everything you need to know to pick the perfect stretch tent dimensions.
      </Paragraph>

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden my-8 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/c2288a635df5.webp"
          alt="Multiple stretch tents of varying sizes at an outdoor event"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Rule of thumb */}
      <SectionHeading>The Golden Rule: Space Per Person</SectionHeading>
      <Paragraph>
        Before diving into specific tent dimensions, it&apos;s important to understand how much space each guest actually needs. This depends heavily on your event format:
      </Paragraph>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
        {[
          { label: 'Seated Dining', space: '1.8 – 2.2 m²', icon: 'ri-restaurant-line' },
          { label: 'Cocktail / Standing', space: '0.7 – 1.0 m²', icon: 'ri-goblet-line' },
          { label: 'Festival / Crowd', space: '0.5 – 0.8 m²', icon: 'ri-music-2-line' },
        ].map((item) => (
          <div key={item.label} className="bg-gray-50 rounded-2xl p-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-3">
              <i className={`${item.icon} text-teal-500 text-lg`}></i>
            </div>
            <p className="font-bold text-gray-900 text-sm mb-1">{item.label}</p>
            <p className="text-teal-600 font-extrabold text-xl">{item.space}</p>
            <p className="text-gray-400 text-xs mt-1">per person</p>
          </div>
        ))}
      </div>

      <TipBox>
        Always add 15–20% buffer space on top of your floor plan calculations to account for pillars, rigging points, walkways, and staging equipment.
      </TipBox>

      {/* Sizing table */}
      <SectionHeading>Stretch Tent Size Reference Guide</SectionHeading>
      <Paragraph>
        Use the table below as a starting point. These are general guidelines, and your specific site conditions and event layout may require adjustments.
      </Paragraph>
      <SizingTable />

      <WarningBox>
        These capacities assume a standard mixed-use layout. For fully seated dining events, reduce the guest count per tent size by approximately 20–30% to ensure comfort.
      </WarningBox>

      {/* Key factors */}
      <SectionHeading>6 Key Factors That Affect Your Tent Size</SectionHeading>
      <Paragraph>
        Guest count alone doesn&apos;t tell the whole story. Here are the factors our team assesses before recommending a tent size for any event:
      </Paragraph>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {factors.map((f) => (
          <FactorCard key={f.title} {...f} />
        ))}
      </div>

      {/* By event type */}
      <SectionHeading>Recommended Sizes by Event Type</SectionHeading>
      <Paragraph>
        Different events have very different spatial demands. Here&apos;s what we typically recommend based on event format:
      </Paragraph>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {eventTypes.map((e) => (
          <EventTypeCard key={e.title} {...e} />
        ))}
      </div>

      {/* Linking tents */}
      <SectionHeading>Linking Multiple Tents for Larger Events</SectionHeading>
      <Paragraph>
        One of the greatest advantages of stretch tents is their ability to be linked together seamlessly. Instead of renting one enormous structure, linking two or three medium-sized tents allows you to:
      </Paragraph>
      <ul className="space-y-3 my-5 ml-1">
        {[
          'Create distinct zones for dining, bar, entertainment, and lounge',
          'Manage crowd flow and emergency egress more effectively',
          'Adapt to irregular terrain or obstacles in your venue',
          'Scale up or down your footprint depending on final guest numbers',
          'Add visual interest with different heights and orientations',
        ].map((item) => (
          <li key={item} className="flex items-start gap-3 text-gray-600 text-sm leading-relaxed">
            <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded-full shrink-0 mt-0.5">
              <i className="ri-check-line text-teal-600 text-xs"></i>
            </span>
            {item}
          </li>
        ))}
      </ul>

      {/* Image 2 */}
      <div className="rounded-2xl overflow-hidden my-8 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/b52507216157.webp"
          alt="Linked stretch tents creating multiple zones at a large outdoor event"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Mistakes */}
      <SectionHeading>Common Sizing Mistakes to Avoid</SectionHeading>

      <SubHeading>1. Sizing for Maximum Capacity Only</SubHeading>
      <Paragraph>
        Many clients size purely for their maximum guest count, forgetting that a tent packed to the rafters feels uncomfortable. Aim for 80% occupancy as your baseline. This leaves room to breathe and gives the space an atmosphere rather than feeling like a crowded train.
      </Paragraph>

      <SubHeading>2. Forgetting the Buffer Zone</SubHeading>
      <Paragraph>
        Entrances, emergency exits, bar setups, DJ or band stages, and catering stations all consume real floor space. A 10m × 15m tent might house 150 people, but if 30% of the floor is taken up by staging, you&apos;re realistically looking at 100 comfortable guests.
      </Paragraph>

      <SubHeading>3. Not Accounting for Rigging Points</SubHeading>
      <Paragraph>
        Stretch tents require anchor points around their perimeter. On soft ground, stakes are used; on hard surfaces like concrete or pavement, weighted ballast systems or structural fixing points are needed. In tight venues, rigging constraints can reduce your usable tent footprint.
      </Paragraph>

      <SubHeading>4. Ignoring Height</SubHeading>
      <Paragraph>
        Stretch tents aren&apos;t flat. Their signature curved profile means central heights can reach 5–6 metres while perimeter edges drop lower. Tall staging, truss lighting rigs, or hanging decor elements need to account for the tent&apos;s profile, not just its floor plan.
      </Paragraph>

      <TipBox>
        When in doubt, go one size up. A slightly larger tent gives you flexibility to add last-minute guests, expand your décor setup, or simply create a more comfortable, airy atmosphere.
      </TipBox>

      {/* Practical checklist */}
      <SectionHeading>Your Pre-Booking Checklist</SectionHeading>
      <Paragraph>
        Before reaching out for a quote, gather this information, as it will help our team recommend the ideal tent configuration for your event:
      </Paragraph>
      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            'Confirmed or estimated guest count',
            'Event type (seated, standing, mixed)',
            'Event date and duration',
            'Venue address and site dimensions',
            'Any obstacles (trees, posts, slopes)',
            'Floor surface type (grass, concrete, gravel)',
            'Equipment needs (stage, bar, catering)',
            'Local weather or wind concerns',
            'Decor or branding requirements',
            'Access road width for delivery vehicles',
          ].map((item, i) => (
            <label key={item} className="flex items-center gap-3 cursor-pointer group">
              <span className="w-5 h-5 flex items-center justify-center border-2 border-teal-300 rounded group-hover:border-teal-500 transition-colors shrink-0">
                <span className="text-teal-400 text-xs font-bold">{i + 1}</span>
              </span>
              <span className="text-gray-700 text-sm">{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Final thoughts */}
      <SectionHeading>Final Thoughts</SectionHeading>
      <Paragraph>
        Selecting the right stretch tent size is as much an art as a science. Our team has sized and installed thousands of tents across every conceivable event type, from intimate sunset dinners to 5,000-person music festivals. If you&apos;re unsure which configuration is right for your venue and crowd, we&apos;re always happy to do a free site assessment and recommendation.
      </Paragraph>
      <Paragraph>
        Ready to explore your options? Browse our full range of stretch tent sizes or get in touch with our team for a tailored quote.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/standard-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          View Standard Tents
        </Link>
        <Link
          to="/products/custom-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-pencil-ruler-2-line"></i>
          Custom Tent Options
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Request a Quote
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Stretch Tents', 'Event Planning', 'Tent Sizing', 'Outdoor Events', 'Wedding Tents', 'Festival Tents'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
